import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../Servicios/supabaseClient.js';
import { Button, Card, Field, Icon } from '../../Components/ui/ui.jsx';
import { formatDate, initials } from './format.js';

const PLATFORMS = ['Gmail', 'Mercado Pago', 'Tiendanube', 'WhatsApp Business', 'Otros'];
const EMPTY = { platform: 'Otros', reference_name: '', username: '', password: '' };

export default function Cuentas() {
    const { user, notify } = useOutletContext();

    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [confirmingId, setConfirmingId] = useState(null);
    const [saving, setSaving] = useState(false);

    const fetchAccounts = useCallback(async () => {
        // No traemos password_field: nunca se muestra después de guardarla
        const { data, error } = await supabase
            .from('linked_accounts')
            .select('id, platform, reference_name, username, created_at')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });
        if (error) {
            console.error('Error trayendo cuentas:', error.message);
            notify('No pudimos cargar tus cuentas. Recargá la página.', 'danger');
        } else {
            setAccounts(data || []);
        }
        setLoading(false);
    }, [user, notify]);

    useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

    const startEdit = (acc) => {
        setEditingId(acc.id);
        setConfirmingId(null);
        setForm({
            platform: PLATFORMS.includes(acc.platform) ? acc.platform : 'Otros',
            reference_name: acc.reference_name || '',
            username: acc.username || '',
            password: '',
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setForm(EMPTY);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editingId) {
                const updates = {
                    platform: form.platform,
                    reference_name: form.reference_name,
                    username: form.username,
                };
                // Solo actualizamos la contraseña si escribieron una nueva
                if (form.password.trim() !== '') updates.password_field = form.password;

                const { error } = await supabase.from('linked_accounts').update(updates).eq('id', editingId);
                if (error) throw error;
                notify('Cuenta actualizada.');
            } else {
                const { error } = await supabase.from('linked_accounts').insert([{
                    user_id: user.id,
                    platform: form.platform,
                    reference_name: form.reference_name,
                    username: form.username,
                    password_field: form.password,
                }]);
                if (error) throw error;
                notify(`Cuenta "${form.reference_name}" vinculada.`);
            }
            cancelEdit();
            fetchAccounts();
        } catch (err) {
            notify(`No pudimos guardar la cuenta: ${err.message}`, 'danger');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (acc) => {
        const { error } = await supabase.from('linked_accounts').delete().eq('id', acc.id);
        if (error) {
            notify('No pudimos desvincular la cuenta. Probá de nuevo.', 'danger');
            return;
        }
        setAccounts((list) => list.filter((a) => a.id !== acc.id));
        setConfirmingId(null);
        if (editingId === acc.id) cancelEdit();
        notify(`Cuenta "${acc.reference_name}" desvinculada.`);
    };

    return (
        <div className="page">
            <header className="pageHeaderText">
                <h1 className="pageTitle">Cuentas linkeadas</h1>
                <p className="pageSubtitle">Las cuentas que tus flujos usan para trabajar por vos: casillas de mail, tiendas, cobros y otras plataformas.</p>
            </header>

            <div className="split">
                <div className="grid2">
                    {accounts.length === 0 ? (
                        <div className="emptyBox">
                            {loading ? 'Cargando cuentas…' : 'No hay cuentas vinculadas. Sumá la primera desde el formulario.'}
                        </div>
                    ) : accounts.map((acc) => {
                        const confirming = confirmingId === acc.id;
                        const platform = acc.platform || 'Otros';
                        return (
                            <article
                                key={acc.id}
                                className={`accountCard${confirming ? ' isConfirming' : ''}${editingId === acc.id ? ' isEditing' : ''}`}
                            >
                                <div className="accountHead">
                                    <span className="accountMark" aria-hidden="true">
                                        {initials(platform !== 'Otros' ? platform : acc.reference_name)}
                                    </span>
                                    <div className="cellStack" style={{ minWidth: 0 }}>
                                        <span className="accountTitle">{acc.reference_name}</span>
                                        <span className="cellSub">{platform}</span>
                                    </div>
                                </div>
                                <dl className="accountFacts">
                                    <dt>Usuario</dt>
                                    <dd title={acc.username}>{acc.username}</dd>
                                    <dt>Contraseña</dt>
                                    <dd className="lockLine"><Icon name="lock" size={14} />Guardada</dd>
                                    <dt>Vinculada</dt>
                                    <dd className="num">{formatDate(acc.created_at)}</dd>
                                </dl>
                                <div className="accountActions">
                                    {confirming ? (
                                        <>
                                            <span className="confirmText">¿Desvincular? Los flujos que la usan dejan de andar.</span>
                                            <button type="button" className="txtBtn" onClick={() => setConfirmingId(null)}>Cancelar</button>
                                            <Button size="sm" variant="danger" onClick={() => handleDelete(acc)}>Desvincular</Button>
                                        </>
                                    ) : (
                                        <>
                                            <button type="button" className="txtBtn txtAccent" onClick={() => startEdit(acc)}>Editar</button>
                                            <button type="button" className="txtBtn txtDanger" onClick={() => setConfirmingId(acc.id)}>Desvincular</button>
                                        </>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>

                <Card featured>
                    <div className="cellStack">
                        <h2 className="qn-card-title">{editingId ? 'Editar cuenta' : 'Vincular cuenta'}</h2>
                        <p className="cardText">
                            {editingId
                                ? 'Dejá la contraseña vacía para mantener la que ya está guardada.'
                                : 'Cargá el acceso una vez y tus flujos lo usan cuando lo necesitan.'}
                        </p>
                    </div>
                    <form className="form" onSubmit={handleSubmit}>
                        <div className="qn-field">
                            <label className="qn-field-label" htmlFor="acc-platform">Plataforma</label>
                            <select
                                id="acc-platform"
                                className="qn-select"
                                value={form.platform}
                                onChange={(e) => setForm({ ...form, platform: e.target.value })}
                            >
                                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                        <Field
                            id="acc-ref"
                            label="Nombre de referencia"
                            placeholder="Ej: Ventas principal"
                            hint="Para reconocerla en la lista y en tus flujos."
                            required
                            value={form.reference_name}
                            onChange={(e) => setForm({ ...form, reference_name: e.target.value })}
                        />
                        <Field
                            id="acc-user"
                            label="Usuario o email"
                            placeholder="usuario@ejemplo.com"
                            required
                            value={form.username}
                            onChange={(e) => setForm({ ...form, username: e.target.value })}
                        />
                        <Field
                            id="acc-pass"
                            label={editingId ? 'Nueva contraseña o token (opcional)' : 'Contraseña o token'}
                            type="password"
                            autoComplete="new-password"
                            placeholder={editingId ? 'Dejar vacío para mantener' : '••••••••'}
                            hint="No la mostramos nunca después de guardarla."
                            required={!editingId}
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                        />
                        <div className="formActions formActionsFill">
                            {editingId && <Button onClick={cancelEdit} disabled={saving}>Cancelar</Button>}
                            <Button type="submit" variant="primary" disabled={saving}>
                                {saving ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Vincular cuenta'}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
}
